import { ISelectItem, TOnItemsFetchedCallback } from "@components/inputs/select/Select.types";
import { IFieldInfoProperties, TGetValueFn } from "@components/smart/FieldInfo";
import { getAddressGroup } from "@components/smart/GeneralFieldDefinition";
import BindingContext from "@odata/BindingContext";
import { AddressEntity, ICountryEntity, RegZecEmploymentEntity } from "@odata/GeneratedEntityTypes";
import { CountryCode, YesNoCode } from "@odata/GeneratedEnums";
import { searchAddress } from "@utils/address.utils";
import { isDefined } from "@utils/general";

import { FieldType, NavigationSource } from "../../../../enums";
import { Model } from "../../../../model/Model";
import { FormStorage } from "../../../../views/formView/FormStorage";

export const checkPathIsNonCZCountry = (storage: Model, path: string): boolean => {
    const country = storage.getValueByPath(path) as ICountryEntity;
    return country && country?.Code !== CountryCode.CzechRepublic;
};

const addressFieldOrder = getAddressGroup(null, { multiline: false, addHouseStreetNumber: true })[0].map(f => f.id);

export function getJmhzIdDef(n: number): Partial<IFieldInfoProperties> {
    return {
        metadata: { id: `1${n.toString().padStart(4, "0")}` },
    };
}

export function getJmhzAddressIdDef(fieldId: string, n: number): Partial<IFieldInfoProperties> {
    const idx = addressFieldOrder.findIndex(id => fieldId.endsWith(id));
    return getJmhzIdDef(n + idx);
}

export function getComboboxDefWithMpsvCode(EntityWithMpsvCodeEntity: { MpsvCode: string, Name: string }): Partial<IFieldInfoProperties> {
    return {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: EntityWithMpsvCodeEntity.MpsvCode,
            shouldDisplayAdditionalColumns: true,
            orderBy: {
                propertyName: EntityWithMpsvCodeEntity.MpsvCode,
                asc: true
            },
        },
        columns: [
            { id: EntityWithMpsvCodeEntity.MpsvCode },
            { id: EntityWithMpsvCodeEntity.Name }
        ],
    };
}

/* do not add AddressEntity.PostalCode, postal code has own validation based on country */
export const REQUIRED_ADDRESS_FIELDS: string[] = [AddressEntity.City, AddressEntity.Country, "HouseNumber"];

/**
 * Function calls the callback to get relative bc to the current navigation and if the final BC value has any used
 * truthy value, returns true to show the field, otherwise the field is hidden.
 * @param navigate
 */
export function ifTruthy(navigate: ((bc: BindingContext) => BindingContext)): TGetValueFn<boolean> {
    return (args)=> {
        const { bindingContext, storage } = args;
        const bc = navigate(bindingContext);
        const val = storage.getValue(bc, { useDirectValue: false });
        return val === true || val === YesNoCode.Yes;
    };
}

export function oneOfIsDefined(navigate: (bc: BindingContext) => BindingContext[]): TGetValueFn<boolean> {
    return (args) => {
        const { bindingContext, storage } = args;
        return navigate(bindingContext).some(bc => {
            const val = storage.getValue(bc, { useDirectValue: false });
            return isDefined(val);
        });
    };
}

export const isInMonths = (months: number[]): TGetValueFn<boolean> => {
    return ({ storage }) => {
        const { Month } = storage.data.entity.Detail ?? {};
        return months.includes(Month);
    };
};

export function isNonZeroValue(navigate: (bc: BindingContext) => BindingContext): TGetValueFn<boolean> {
    return (args) => {
        const { bindingContext, storage } = args;
        const bc = navigate(bindingContext);
        const val = storage.getValue(bc, { useDirectValue: false });
        return isDefined(val) && val !== 0;
    };
}

type TGetAddressFields = "City" | "AddressCode";
type TGetAddressFieldDefFn = (searchBy: TGetAddressFields) => IFieldInfoProperties;
const getHandleFetchFn = (field: TGetAddressFields): ((value: string, highlightCallback?: TOnItemsFetchedCallback) => void) => {
    return (value, highlightCallback) => {
        searchAddress(value)
            .then(data => {
                let items: ISelectItem[] = [];
                if (data?.length) {
                    items = data.map(address => {
                        const tabularData = [address.AddressCode, address.Region];
                        if (field === "City") {
                            tabularData.reverse();
                        }
                        return {
                            id: `Autocomplete-field-${address.AddressCode}`,
                            label: `${address.Region} ${address.AddressCode}`,
                            tabularData,
                            additionalData: {
                                City: address.City,
                                AddressCode: address.AddressCode,
                            }
                        };
                    });
                }
                highlightCallback(items);
            });
    };
};

// Definition of fields, which loads data from search address API and autocompletes them to the field
export const getAddressAutocompleteDef: TGetAddressFieldDefFn = (searchBy) => {
    return {
        type: FieldType.Autocomplete,
        fieldSettings: {
            allowCreate: true,
            items: [],
            useAutoSelection: false,
            useTypingForward: false,
            minCharsToOpenMenu: 2,
            onItemsFetchRequested: getHandleFetchFn(searchBy),
            localDependentFields: [{
                from: { id: "City" },
                to: { id: RegZecEmploymentEntity.City },
                navigateFrom: NavigationSource.Parent
            }, {
                from: { id: "AddressCode" },
                to: { id: RegZecEmploymentEntity.CityCode },
                navigateFrom: NavigationSource.Parent
            }],
            shouldDisplayAdditionalColumns: true
        },
        affectedFields: [
            { id: searchBy === "City" ? RegZecEmploymentEntity.CityCode : RegZecEmploymentEntity.City, navigateFromParent: true },
        ]
    };
};

export async function defaultCityCode(storage: FormStorage, pathToCityCode: string): Promise<void> {
    if (!storage.data.bindingContext.isValidNavigation(pathToCityCode)) {
        return;
    }
    const cityCodeBc = storage.data.bindingContext.navigate(pathToCityCode);
    const cityCode = storage.getValue(cityCodeBc);
    const cityBc = cityCodeBc.getParent().navigate("City");
    const city = storage.getValue(cityBc);
    if (city && !cityCode) {
        // default only when not defined
        const data = await searchAddress(city);
        const exactMath = data?.filter(item => item.City === city);
        if (exactMath?.length === 1) {
            storage.setValue(cityCodeBc, exactMath[0].AddressCode);
            storage.refreshFields(true);
        }
    }
}

